import { useEffect, useState } from 'react';
import { authApi, type Lead, type UsuarioAdmin, type PlanTier } from '../lib/api';

const FUENTE_TONE: Record<string, { soft: string; ink: string; solid: string; line: string }> = {
  instagram:           { soft: 'var(--copper-soft)', ink: 'var(--copper-ink)', solid: 'var(--copper)', line: 'var(--copper-line)' },
  instagram_comentario: { soft: 'var(--copper-soft)', ink: 'var(--copper-ink)', solid: 'var(--copper)', line: 'var(--copper-line)' },
  formulario:          { soft: 'var(--sage-soft)',   ink: 'var(--sage-ink)',   solid: 'var(--sage)',   line: 'var(--sage-line)'   },
  directo:             { soft: 'var(--info-soft)',   ink: '#3c5567',           solid: 'var(--info)',   line: '#cbd9e2'             },
  dm:                  { soft: 'var(--amber-soft)',  ink: 'var(--amber)',      solid: 'var(--amber)',  line: '#e7d7ab'             },
};

const PLAN_BADGE: Record<PlanTier, { label: string; bg: string; ink: string }> = {
  libre: { label: 'Gratis', bg: 'var(--surface-3)', ink: 'var(--ink-2)' },
  plus:  { label: 'Plus',   bg: 'var(--copper-soft)', ink: 'var(--copper-ink)' },
  pro:   { label: 'Pro',    bg: 'var(--ink)', ink: '#f4efe8' },
  max:   { label: 'Max ✦',  bg: 'var(--amber-soft)', ink: 'var(--amber)' },
};

const AV_GRADIENTS = [
  { a: '#c49a7a', b: '#b0744f' },
  { a: '#8aa67e', b: '#6f8a64' },
  { a: '#8faabb', b: '#6080a0' },
  { a: '#c9a86c', b: '#a07a40' },
  { a: '#b07a8a', b: '#8a5060' },
  { a: '#7a9a8a', b: '#507a6a' },
];

type SegVal = 'todos' | 'registrados' | 'solo';
type Tab = 'instagram' | 'usuarios';

// D1's datetime('now') returns "YYYY-MM-DD HH:MM:SS" (no timezone marker) which
// JS parses as local time instead of UTC. Normalize before parsing precise times.
function parseUtcDate(value: string): Date {
  const hasTimezone = /[zZ]|[+-]\d\d:\d\d$/.test(value);
  return new Date(hasTimezone ? value : `${value.replace(' ', 'T')}Z`);
}

function fmtDate(iso: string): string {
  const d = parseUtcDate(iso);
  return `${d.getDate()} ${['ene','feb','mar','abr','may','jun','jul','ago','sep','oct','nov','dic'][d.getMonth()]} ${d.getFullYear()}`;
}

function fmtDateTime(iso: string): string {
  const d = parseUtcDate(iso);
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  return `${fmtDate(iso)} · ${hh}:${mm}`;
}

function initials(nombre: string | null, email: string): string {
  if (nombre?.trim()) {
    const p = nombre.trim().split(' ');
    return (p[0][0] + (p[1]?.[0] ?? '')).toUpperCase();
  }
  return email.slice(0, 2).toUpperCase();
}

function Avatar({ name, email, fotoUrl, idx }: { name: string | null; email: string; fotoUrl?: string | null; idx: number }) {
  if (fotoUrl) {
    return (
      <img src={fotoUrl} alt={name ?? email} style={{
        width: 34, height: 34, borderRadius: 99, flexShrink: 0, objectFit: 'cover',
        border: '1.5px solid rgba(255,255,255,.35)', boxShadow: '0 1px 4px rgba(0,0,0,.12)',
      }} />
    );
  }
  const g = AV_GRADIENTS[idx % AV_GRADIENTS.length];
  return (
    <span style={{ width: 34, height: 34, borderRadius: 99, flexShrink: 0, display: 'grid', placeItems: 'center',
      background: `linear-gradient(135deg, ${g.a}, ${g.b})`, color: '#fff',
      fontFamily: 'var(--sans)', fontSize: 12, fontWeight: 700, letterSpacing: '0.02em',
      border: '1.5px solid rgba(255,255,255,.35)', boxShadow: '0 1px 4px rgba(0,0,0,.12)' }}>
      {initials(name, email)}
    </span>
  );
}

/* ════════════════════════════════════════════════════════════════════════
   Tab 1 — Leads de Instagram
   ════════════════════════════════════════════════════════════════════════ */
function LeadsInstagramTab() {
  const [leads, setLeads]   = useState<Lead[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]   = useState('');
  const [search, setSearch] = useState('');
  const [seg, setSeg]       = useState<SegVal>('todos');
  const [fuente, setFuente] = useState('');

  useEffect(() => {
    authApi.getLeads()
      .then(r => setLeads(r.leads))
      .catch(e => setError(e instanceof Error ? e.message : 'Error'))
      .finally(() => setLoading(false));
  }, []);

  const fuentes = [...new Set(leads.map(l => l.fuente).filter(Boolean))] as string[];
  const withIg  = leads.filter(l => l.instagram_username);

  const filtered = leads.filter(l => {
    if (seg === 'registrados' && !l.instagram_username) return false;
    if (seg === 'solo' && l.instagram_username) return false;
    if (fuente && l.fuente !== fuente) return false;
    if (search.trim()) {
      const q = search.toLowerCase();
      return (l.nombre ?? '').toLowerCase().includes(q)
        || l.email.toLowerCase().includes(q)
        || (l.instagram_username ?? '').toLowerCase().includes(q);
    }
    return true;
  });

  const SEG_OPTS: { val: SegVal; label: string }[] = [
    { val: 'todos',       label: 'Todos'       },
    { val: 'registrados', label: 'Registrados' },
    { val: 'solo',        label: 'Solo lead'   },
  ];

  return (
    <>
      {/* Stats */}
      <div className="grid" style={{ gridTemplateColumns: 'repeat(3,1fr)', marginBottom: 'var(--gap)' }}>
        <div className="card stat fade-up">
          <div className="stat-top">
            <span className="stat-ico" style={{ background: 'var(--copper-soft)', color: 'var(--copper)' }}><UsersIco /></span>
          </div>
          <div className="stat-num tabular">{leads.length}</div>
          <div className="stat-label">Leads totales</div>
          <div className="stat-delta flat">{filtered.length} mostrados</div>
        </div>
        <div className="card stat fade-up">
          <div className="stat-top">
            <span className="stat-ico" style={{ background: 'var(--sage-soft)', color: 'var(--sage)' }}><CheckIco /></span>
          </div>
          <div className="stat-num tabular">{withIg.length}</div>
          <div className="stat-label">Registrados en portal</div>
          <div className="stat-delta flat">{leads.length - withIg.length} solo lead</div>
        </div>
        <div className="card stat fade-up">
          <div className="stat-top">
            <span className="stat-ico" style={{ background: 'var(--info-soft)', color: 'var(--info)' }}><GlobeIco /></span>
          </div>
          <div className="stat-num tabular">{fuentes.length}</div>
          <div className="stat-label">Fuentes activas</div>
          <div className="stat-delta flat">{fuentes.join(', ') || '—'}</div>
        </div>
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      <div className="card">
        {/* Filters row */}
        <div className="card-pad" style={{ paddingBottom: 0 }}>
          {/* Search */}
          <div className="row" style={{ gap: 10, marginBottom: 12 }}>
            <div style={{ position: 'relative', flex: 1 }}>
              <span style={{ position: 'absolute', left: 11, top: '50%', transform: 'translateY(-50%)', color: 'var(--muted)', pointerEvents: 'none' }}>
                <SearchIco size={15} />
              </span>
              <input className="input" value={search} onChange={e => setSearch(e.target.value)}
                placeholder="Buscar por nombre, email o @usuario…"
                style={{ paddingLeft: 34, height: 36, fontSize: 13.5 }} />
            </div>
            <select className="filter-select" value={fuente} onChange={e => setFuente(e.target.value)}
              style={{ height: 36, fontSize: 12.5 }}>
              <option value="">Todas las fuentes</option>
              {fuentes.map(f => <option key={f} value={f}>{f}</option>)}
            </select>
          </div>

          {/* Seg control */}
          <div className="row" style={{ gap: 0, marginBottom: 14 }}>
            <div className="seg">
              {SEG_OPTS.map(o => (
                <button key={o.val} className={seg === o.val ? 'on' : ''}
                  onClick={() => setSeg(o.val)}>
                  {o.label}
                </button>
              ))}
            </div>
            <div style={{ flex: 1 }} />
            <span className="muted" style={{ fontSize: 12.5 }}>{filtered.length} contacto{filtered.length !== 1 ? 's' : ''}</span>
          </div>
        </div>
        <hr className="hr" />

        {loading && <div className="loading-center"><span className="spinner" /> Cargando leads…</div>}

        {!loading && filtered.length === 0 && (
          <div style={{ padding: '60px 20px', textAlign: 'center' }}>
            <span style={{ width: 52, height: 52, borderRadius: 16, background: 'var(--surface-2)', display: 'grid', placeItems: 'center', margin: '0 auto 14px', color: 'var(--ink-2)' }}>
              <UsersIco size={24} />
            </span>
            <div style={{ fontFamily: 'var(--serif)', fontSize: 17, color: 'var(--ink)' }}>Sin contactos</div>
            <div style={{ fontSize: 13, marginTop: 4, color: 'var(--muted)' }}>
              {search || fuente || seg !== 'todos' ? 'Prueba ajustando los filtros' : 'Los leads aparecerán aquí cuando alguien complete el flujo de Instagram (comentario → DM → guía)'}
            </div>
          </div>
        )}

        {!loading && filtered.length > 0 && (
          <table className="tbl">
            <thead>
              <tr>
                <th>Persona</th>
                <th>Categoría</th>
                <th>Fuente</th>
                <th>Instagram</th>
                <th>Fecha de contacto</th>
                <th style={{ textAlign: 'right' }}>Estado del flujo</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((lead, idx) => {
                const ft  = lead.fuente ? (FUENTE_TONE[lead.fuente.toLowerCase()] ?? FUENTE_TONE.formulario) : null;
                const hasIg = !!lead.instagram_username;
                return (
                  <tr key={lead.id}>
                    <td>
                      <div className="row" style={{ gap: 10 }}>
                        <Avatar name={lead.nombre} email={lead.email} idx={idx} />
                        <div style={{ minWidth: 0 }}>
                          <div style={{ fontWeight: 600, fontSize: 13.5, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {lead.nombre ?? <span style={{ color: 'var(--muted)', fontWeight: 400 }}>Sin nombre</span>}
                          </div>
                          <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 1 }}>{lead.email}</div>
                        </div>
                      </div>
                    </td>
                    <td>
                      {lead.categoria
                        ? <span className="tag">{lead.categoria}</span>
                        : <span className="muted">—</span>}
                    </td>
                    <td>
                      {ft && lead.fuente
                        ? <span className="tag" style={{ background: ft.soft, color: ft.ink, borderColor: ft.line, height: 22 }}>
                            <span className="dot" style={{ background: ft.solid }} />
                            {lead.fuente}
                          </span>
                        : <span className="muted">—</span>
                      }
                    </td>
                    <td>
                      {hasIg
                        ? <span className="row" style={{ gap: 6, fontSize: 13 }}>
                            <IgIco size={14} />
                            <span style={{ color: 'var(--sage)' }}>@{lead.instagram_username}</span>
                          </span>
                        : <span className="muted">—</span>
                      }
                    </td>
                    <td className="muted" style={{ fontSize: 12.5 }}>{fmtDate(lead.created_at)}</td>
                    <td style={{ textAlign: 'right' }}>
                      {lead.flujo_estado === 'completado' ? (
                        <span className="chip" style={{ background: 'var(--sage-soft)', color: 'var(--sage-ink)', borderColor: 'var(--sage-line)' }}>
                          <span className="dot" style={{ background: 'var(--sage)' }} /> Completado
                        </span>
                      ) : lead.flujo_estado === 'parcial' ? (
                        <span className="chip" style={{ background: 'var(--amber-soft)', color: 'var(--amber)' }}>
                          <span className="dot" style={{ background: 'var(--amber)' }} /> Parcial
                        </span>
                      ) : (
                        <span className="tag" style={{ height: 22, background: 'var(--surface-2)', color: 'var(--ink-2)' }}>
                          Sin DM
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </>
  );
}

/* ════════════════════════════════════════════════════════════════════════
   Tab 2 — Usuarios registrados
   ════════════════════════════════════════════════════════════════════════ */
function UsuarioDetailModal({ usuario, onClose }: { usuario: UsuarioAdmin; onClose: () => void }) {
  const plan = PLAN_BADGE[usuario.plan_tier];
  return (
    <div className="modal" onClick={onClose} style={{ background: 'rgba(47,37,25,.38)', backdropFilter: 'blur(2px)' }}>
      <div className="modal-box" onClick={(e) => e.stopPropagation()}>
        <div style={{ padding: '22px 24px 0' }}>
          <div className="row" style={{ gap: 12, alignItems: 'flex-start' }}>
            <Avatar name={usuario.nombre} email={usuario.email} fotoUrl={usuario.foto_url ?? usuario.avatar_url} idx={0} />
            <div style={{ minWidth: 0, flex: 1 }}>
              <h3 style={{ margin: 0, fontSize: 17 }}>{usuario.nombre}</h3>
              <p className="muted" style={{ margin: '2px 0 0', fontSize: 12.5 }}>{usuario.email}</p>
            </div>
            <button className="btn btn-ghost btn-icon" onClick={onClose}><XIco size={17} /></button>
          </div>
        </div>
        <hr className="hr" style={{ margin: '18px 0 0' }} />

        <div style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div>
            <div className="field-label">Plan actual</div>
            <span className="tag" style={{ background: plan.bg, color: plan.ink }}>{plan.label}</span>
          </div>

          <div>
            <div className="field-label">Sobre mí</div>
            <div style={{ fontSize: 13.5, color: usuario.bio ? 'var(--ink)' : 'var(--muted)', lineHeight: 1.55 }}>
              {usuario.bio || 'Sin descripción'}
            </div>
          </div>

          <div className="row" style={{ gap: 24, flexWrap: 'wrap' }}>
            <div>
              <div className="field-label">Registrado</div>
              <div style={{ fontSize: 13.5 }}>{fmtDateTime(usuario.created_at)}</div>
            </div>
            <div>
              <div className="field-label">Fuente</div>
              <div className="row" style={{ gap: 6, fontSize: 13.5 }}>
                {usuario.google_id ? <><GoogleIco size={14} /> Google</> : 'Email y contraseña'}
              </div>
            </div>
            <div>
              <div className="field-label">Posts en el foro</div>
              <div style={{ fontSize: 13.5 }}>{usuario.posts_count}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function UsuariosTab() {
  const [usuarios, setUsuarios] = useState<UsuarioAdmin[]>([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState('');
  const [search, setSearch]     = useState('');
  const [detail, setDetail]     = useState<UsuarioAdmin | null>(null);

  useEffect(() => {
    authApi.getUsuarios()
      .then(r => setUsuarios(r.usuarios))
      .catch(e => setError(e instanceof Error ? e.message : 'Error'))
      .finally(() => setLoading(false));
  }, []);

  const conGoogle = usuarios.filter(u => u.google_id);
  const conPlanPago = usuarios.filter(u => u.plan_tier !== 'libre');

  const filtered = usuarios.filter(u => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return u.nombre.toLowerCase().includes(q) || u.email.toLowerCase().includes(q);
  });

  return (
    <>
      <div className="grid" style={{ gridTemplateColumns: 'repeat(3,1fr)', marginBottom: 'var(--gap)' }}>
        <div className="card stat fade-up">
          <div className="stat-top">
            <span className="stat-ico" style={{ background: 'var(--copper-soft)', color: 'var(--copper)' }}><UsersIco /></span>
          </div>
          <div className="stat-num tabular">{usuarios.length}</div>
          <div className="stat-label">Usuarios registrados</div>
        </div>
        <div className="card stat fade-up">
          <div className="stat-top">
            <span className="stat-ico" style={{ background: 'var(--info-soft)', color: 'var(--info)' }}><GoogleIco /></span>
          </div>
          <div className="stat-num tabular">{conGoogle.length}</div>
          <div className="stat-label">Vía Google</div>
        </div>
        <div className="card stat fade-up">
          <div className="stat-top">
            <span className="stat-ico" style={{ background: 'var(--sage-soft)', color: 'var(--sage)' }}><CheckIco /></span>
          </div>
          <div className="stat-num tabular">{conPlanPago.length}</div>
          <div className="stat-label">Con plan de pago</div>
        </div>
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      <div className="card">
        <div className="card-pad" style={{ paddingBottom: 14 }}>
          <div style={{ position: 'relative' }}>
            <span style={{ position: 'absolute', left: 11, top: '50%', transform: 'translateY(-50%)', color: 'var(--muted)', pointerEvents: 'none' }}>
              <SearchIco size={15} />
            </span>
            <input className="input" value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Buscar por nombre o email…"
              style={{ paddingLeft: 34, height: 36, fontSize: 13.5, maxWidth: 360 }} />
          </div>
        </div>
        <hr className="hr" />

        {loading && <div className="loading-center"><span className="spinner" /> Cargando usuarios…</div>}

        {!loading && filtered.length === 0 && (
          <div style={{ padding: '60px 20px', textAlign: 'center' }}>
            <span style={{ width: 52, height: 52, borderRadius: 16, background: 'var(--surface-2)', display: 'grid', placeItems: 'center', margin: '0 auto 14px', color: 'var(--ink-2)' }}>
              <UsersIco size={24} />
            </span>
            <div style={{ fontFamily: 'var(--serif)', fontSize: 17, color: 'var(--ink)' }}>Sin usuarios</div>
            <div style={{ fontSize: 13, marginTop: 4, color: 'var(--muted)' }}>
              {search ? 'Prueba ajustando la búsqueda' : 'Los usuarios aparecerán aquí cuando se registren en el portal'}
            </div>
          </div>
        )}

        {!loading && filtered.length > 0 && (
          <table className="tbl">
            <thead>
              <tr>
                <th>Persona</th>
                <th>Plan</th>
                <th>Registrado</th>
                <th>Fuente</th>
                <th style={{ textAlign: 'right' }}>Foro</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((u, idx) => {
                const plan = PLAN_BADGE[u.plan_tier];
                return (
                  <tr key={u.id} onClick={() => setDetail(u)} style={{ cursor: 'pointer' }}>
                    <td>
                      <div className="row" style={{ gap: 10 }}>
                        <Avatar name={u.nombre} email={u.email} fotoUrl={u.foto_url ?? u.avatar_url} idx={idx} />
                        <div style={{ minWidth: 0 }}>
                          <div style={{ fontWeight: 600, fontSize: 13.5, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {u.nombre}
                          </div>
                          <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 1 }}>{u.email}</div>
                        </div>
                      </div>
                    </td>
                    <td><span className="tag" style={{ background: plan.bg, color: plan.ink }}>{plan.label}</span></td>
                    <td className="muted" style={{ fontSize: 12.5 }}>{fmtDate(u.created_at)}</td>
                    <td>
                      {u.google_id
                        ? <span className="row" style={{ gap: 6, fontSize: 13 }}><GoogleIco size={14} /> Google</span>
                        : <span className="muted">Email</span>}
                    </td>
                    <td className="muted" style={{ textAlign: 'right', fontSize: 12.5 }}>{u.posts_count} post{u.posts_count !== 1 ? 's' : ''}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {detail && <UsuarioDetailModal usuario={detail} onClose={() => setDetail(null)} />}
    </>
  );
}

/* ════════════════════════════════════════════════════════════════════════
   Page
   ════════════════════════════════════════════════════════════════════════ */
export default function Leads() {
  const [tab, setTab] = useState<Tab>('instagram');

  return (
    <div className="page page-wide">
      <div style={{ marginBottom: 22 }}>
        <h1 style={{ fontFamily: 'var(--serif)', fontSize: 28, margin: 0 }}>Leads · CRM</h1>
        <p className="muted" style={{ margin: '7px 0 0', fontSize: 14 }}>
          Contactos captados por Instagram y usuarios reales registrados en el portal.
        </p>
      </div>

      <div className="seg" style={{ marginBottom: 22 }}>
        <button className={tab === 'instagram' ? 'on' : ''} onClick={() => setTab('instagram')}>
          Leads de Instagram
        </button>
        <button className={tab === 'usuarios' ? 'on' : ''} onClick={() => setTab('usuarios')}>
          Usuarios registrados
        </button>
      </div>

      {tab === 'instagram' ? <LeadsInstagramTab /> : <UsuariosTab />}
    </div>
  );
}

/* ── Icons ── */
function Svg({ children, size = 18 }: { children: React.ReactNode; size?: number }) {
  return <svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">{children}</svg>;
}
function UsersIco({ size = 18 }: { size?: number }) { return <Svg size={size}><path d="M16 11c1.7 0 3 1.3 3 3s-1.3 3-3 3M8 11C6.3 11 5 12.3 5 14s1.3 3 3 3M12 3a4 4 0 1 1 0 8 4 4 0 0 1 0-8zM20 21c0-2.2-1.8-4-4-4H8c-2.2 0-4 1.8-4 4"/></Svg>; }
function CheckIco({ size = 18 }: { size?: number })  { return <Svg size={size}><path d="M5 12.5l4.5 4.5L19 6.5"/></Svg>; }
function GlobeIco({ size = 18 }: { size?: number })  { return <Svg size={size}><circle cx="12" cy="12" r="9"/><path d="M2 12h20M12 3a15 15 0 0 1 4 9 15 15 0 0 1-4 9 15 15 0 0 1-4-9 15 15 0 0 1 4-9z"/></Svg>; }
function SearchIco({ size = 18 }: { size?: number }) { return <Svg size={size}><circle cx="11" cy="11" r="7"/><path d="m20 20-3-3"/></Svg>; }
function IgIco({ size = 18 }: { size?: number })     { return <Svg size={size}><rect x="3" y="3" width="18" height="18" rx="5"/><circle cx="12" cy="12" r="4"/><circle cx="17.5" cy="6.5" r=".5" fill="currentColor" stroke="none"/></Svg>; }
function XIco({ size = 18 }: { size?: number })      { return <Svg size={size}><path d="M6 6l12 12M18 6 6 18"/></Svg>; }
function GoogleIco({ size = 18 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24">
      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93z"/>
      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
    </svg>
  );
}
